import api, { setAuthToken } from './client';

export type LoginResp = { success?: boolean; message?: string; data?: any };
export type VerifyResp = { token?: string; accessToken?: string; success?: boolean; message?: string; data?: any; refreshToken?: string; tokenCreated?: string; tokenExpires?: string };

export async function signUp(email: string, password: string, companyName?: string, redirectUrl?: string) {
  const { data } = await api.post('/v1/auth/signup', { email, password, companyName, redirectUrl });
  return data;
}

// Access request signup (Request Access modal)
export interface AccessSignupBody {
  firstName: string;
  lastName: string;
  companyName: string;
  businessEmail: string;
  jobTitle: string;
  address: string;
  state: string;
  country: string;
  postCode: string;
  phoneNumber: string;
  businessNeed: string;
}

export async function accessSignup(payload: AccessSignupBody) {
  // Server expects PascalCase endpoint /v1/auth/Signup (note capital S)
  const { data } = await api.post('/v1/auth/Signup', payload);
  return data;
}

export async function login(username: string, password: string): Promise<LoginResp> {
  try {
    
// Get browser-specific local date and time
    //const currentTime = new Date().toLocaleString();
    const currentTime = new Date().toISOString();

    const { data } = await api.post<LoginResp>('/v1/auth/portallogin', { username, password, time: currentTime });
    console.log('[services/auth.login] response', data);
    // If backend returns an access token at login (some flows do), accept and persist it.
    const loginToken = (data as any)?.token || (data as any)?.accessToken || (data as any)?.data?.token || (data as any)?.data?.accessToken;
    if (loginToken) {
      try { setAuthToken(loginToken); } catch (_) {}
    }
    return data;
  } catch (err: any) {
    console.error('[services/auth.login] error', err?.response?.data ?? err?.message ?? err);
    throw err;
  }
}

export async function verifyOtp(username: string, loginOtp: string): Promise<VerifyResp> {
  try {
    const { data } = await api.post<VerifyResp>('/v1/auth/portal-login-authorization', { username, loginOtp });
    console.log('[services/auth.verifyOtp] response', data);

    // Helpful debug: if server responds with a message only, log it too
    if (!data) console.warn('[services/auth.verifyOtp] empty response from server');
    // Accept token either at the top-level (token/accessToken) OR nested inside data.data (some backends return tokens there)
    const token = data?.token || data?.accessToken || data?.data?.token || data?.data?.accessToken;
    if (token) {
      try {
        setAuthToken(token);
      } catch (e) {}
    }

    return data;
  } catch (err: any) {
    console.error('[services/auth.verifyOtp] error', err?.response?.data ?? err?.message ?? err);
    throw err;
  }
}

export async function logout(authUserId?: string | null) {
  try {
    await api.post('/v1/auth/Logout', {}, { params: { authuserId: authUserId || authUserId === '' ? authUserId : undefined } });
  } catch (e) {
    console.warn('[services/auth.logout] backend logout failed', e);
  } finally {
    setAuthToken(null);
  }
}

export async function refreshSession() {
  try {
    // Use refresh-token endpoint which reads HttpOnly refresh cookie and issues a new access token
    const { data } = await api.post('/v1/auth/refresh-token');
    // Support token returned at top-level or nested in `data`
    const token = data?.token || data?.accessToken || data?.data?.token || data?.data?.accessToken;
    if (token) setAuthToken(token);
    return data;
  } catch (err: any) {
    console.error('[services/auth.refreshSession] error', err?.response?.data ?? err?.message ?? err);
    throw err;
  }
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const { data } = await api.post('/v1/auth/change-password', { currentPassword, newPassword });
  return data;
}

// New flow for account Change Password within profile
// Step 1: Initiate change (server can validate session and optionally send OTP)
export async function initiatePasswordChange() {
  // Pull username and authUserId from in-memory store or sessionStorage
  let username: string | null = null;
  let authUserId: string | number | null = null;
  try {
    const raw = sessionStorage.getItem('userData');
    if (raw) {
      const ud = JSON.parse(raw);
      username = ud?.username || ud?.email || ud?.userName || null;
      authUserId = ud?.authUserId || ud?.id || null;
    }
  } catch (_) {}

  const payload: any = { username, authUserId };
  const { data } = await api.post('/v1/auth/change-password', payload);
  return data;
}

// Step 2: Submit new password with OTP
export async function updateNewPassword(params: {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  otp: string;
}) {
  let username: string | null = null;
  let authUserId: string | number | null = null;
  try {
    const raw = sessionStorage.getItem('userData');
    if (raw) {
      const ud = JSON.parse(raw);
      username = ud?.username || ud?.email || ud?.userName || null;
      authUserId = ud?.authUserId || ud?.id || null;
    }
  } catch (_) {}

  const body = {
    oldPassword: params.oldPassword,
    newPassword: params.newPassword,
    confirmNewPassword: params.confirmNewPassword,
    otp: params.otp,
    username,
    authUserId,
  };
  const { data } = await api.post('/v1/auth/update-new-password', body);
  return data;
}

export async function resendOtp(username: string) {
  const { data } = await api.post('/v1/auth/ResendOtp', { username });
  return data;
}

// Assumed endpoints for forgot/reset password flow. Adjust paths/fields if backend differs.
export async function requestPasswordReset(username: string) {
  // Backend expects: ForgotPassword(string username) bound from query (?username=...)
  const { data } = await api.post('/v1/auth/forgot-password', null, { params: { username } });
  return data;
}

export async function resetPassword(params: { username: string; password: string; token: string }) {
  // Backend expects ForgotPassword model with Username, Password, Token at /v1/auth/UpdatePassword
  const { username, password, token } = params;
  const { data } = await api.post('/v1/auth/UpdatePassword', { username, password, token });
  return data;
}

export default {
  signUp,
  login,
  verifyOtp,
  logout,
  refreshSession,
  changePassword,
  resendOtp,
  requestPasswordReset,
  resetPassword,
  initiatePasswordChange,
  updateNewPassword,
  accessSignup,
};
